import type { Attachment, ChatMessage, MessageBlock, NoticeBlock, TextBlock } from '@/api/models';
import { isClientBlockType } from '@/api/models';
import { Markdown } from '@/lib/markdown/Markdown';
import { UnknownTurn } from '@/components/UnknownTurn';
import { cn } from '@/lib/cn';
import { formatBytes, formatStamp } from '@/lib/time';
import { errorCopy } from './copy/errors';

/**
 * The turn renderers.
 *
 * The mapping is deliberately narrow: `text` and `notice` blocks from `agent`
 * and `system` authors, plus the client's own turns. `ask` is handled by the
 * ask card, which is interactive. Everything else - an `action` block, an
 * `expert` author, a block type invented next quarter - routes to
 * <UnknownTurn>, which shouts.
 *
 * That is the second of the two mechanisms behind invariant I7. The first is
 * the server, which physically cannot return an expert turn on this prefix. A
 * renderer that quietly drew whatever arrived would turn a server bug into a
 * silent, permanent leak; a renderer that refuses to draw what it does not
 * recognise turns the same bug into a review-stopping red slab.
 */

// ---------------------------------------------------------------------------
// The client's own turn - a panel card, the only thing in the thread that is
// theirs. What they asked reads at 16.5px, larger than anything the machine
// says back, because it is the sentence the whole run is accountable to.
// ---------------------------------------------------------------------------

export function ClientTurn({ message }: { message: ChatMessage }) {
  const text = message.blocks
    .filter((b): b is TextBlock => b.type === 'text')
    .map((b) => b.text)
    .join('\n\n');

  return (
    <li className="animate-fade-rise">
      <div className="mb-5 mt-3.5 rounded-lg bg-bone px-5 py-4">
        <p className="whitespace-pre-wrap text-[1.03125rem] leading-[1.5] tracking-[-0.015em] text-graphite-900">
          {text}
        </p>
        {message.attachments?.length ? <AttachmentList attachments={message.attachments} /> : null}
        <span className="mt-2 block font-mono text-[0.6875rem] text-graphite-300">
          You · {formatStamp(message.created_at)}
        </span>
      </div>
    </li>
  );
}

function AttachmentList({ attachments }: { attachments: Attachment[] }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-2">
      {attachments.map((a) => (
        <li
          key={a.id}
          className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-bone-raised py-1.5 pl-3.5 pr-3.5 font-mono text-[0.6875rem] text-graphite-500"
        >
          <span className="max-w-[14rem] truncate">{a.filename}</span>
          <span className="text-graphite-300">{formatBytes(a.size_bytes)}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Agent and system turns - inside a run stop, on the rail.
// ---------------------------------------------------------------------------

export function AgentBlocks({ message }: { message: ChatMessage }) {
  return (
    <>
      {message.blocks.map((block, index) => (
        <BlockRenderer key={`${message.id}-${index}`} block={block} messageId={message.id} index={index} />
      ))}
    </>
  );
}

function BlockRenderer({ block, messageId, index }: { block: MessageBlock; messageId: number; index: number }) {
  if (!isClientBlockType(block.type)) {
    return (
      <UnknownTurn
        leakKind="unknown_block_type"
        token={String(block.type).slice(0, 48)}
        context={{ messageId, index }}
      />
    );
  }

  if (block.type === 'text') {
    return <Markdown source={(block as TextBlock).text} />;
  }

  if (block.type === 'notice') {
    return <SystemNotice block={block as NoticeBlock} />;
  }

  // `ask` blocks are rendered by the interactive ask card, which is anchored to
  // the composer end of the thread so an unanswered question cannot scroll away.
  return null;
}

export function SystemNotice({ block }: { block: NoticeBlock }) {
  const tone = block.level;

  // THE CODE IS THE CONTRACT, THE WORDING IS OURS — openapi.yaml says as much
  // on NoticeBlock: "error_code drives the client-facing wording".
  //
  // The server writes a failure notice row carrying only the code, deliberately,
  // so the agent's own failure text can never reach a client. That row projects
  // to a notice with an EMPTY message, and this component used to render exactly
  // that: a bar of alarm colour with nothing written in it, once per failed run.
  const copy = block.error_code ? errorCopy(block.error_code) : null;
  const message = block.message?.trim() ? block.message : copy?.body;

  // A notice with neither a message nor a code says nothing at all. Drawing an
  // empty alarm bar is worse than drawing nothing: it reads as a fault in the
  // dashboard rather than a fault in the run.
  if (!message) return null;

  return (
    <div
      className={cn(
        'mb-[18px] flex items-start gap-3.5 rounded-lg px-[22px] py-4',
        tone === 'error' && 'bg-alarm-wash',
        tone === 'warning' && 'bg-butter',
        tone === 'info' && 'bg-bone',
      )}
    >
      <span
        aria-hidden
        className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-bone-raised/70"
      >
        <svg viewBox="0 0 12 12" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="1.6">
          {tone === 'error' ? (
            <>
              <circle cx="6" cy="6" r="4.6" />
              <path d="M4.2 7.8 7.8 4.2" />
            </>
          ) : (
            <>
              <circle cx="6" cy="6" r="4.6" />
              <path d="M6 3.4V6l1.8 1.4" />
            </>
          )}
        </svg>
      </span>
      <div className="min-w-0">
        {copy ? (
          <p className={cn('font-semibold', tone === 'error' ? 'text-alarm' : 'text-graphite-900')}>{copy.title}</p>
        ) : null}
        <p className={cn('min-w-0 text-[0.9375rem] text-graphite-500', copy && 'mt-1')}>{message}</p>
      </div>
    </div>
  );
}

/**
 * Assistant text arriving as deltas, before the canonical `message` frame
 * replaces it. Rendered through the same strict markdown path - a half-written
 * document is exactly as untrusted as a finished one.
 */
export function StreamingText({ text }: { text: string }) {
  return (
    <div className="relative mb-[22px]">
      <Markdown source={text} />
      <span aria-hidden className="ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.15em] animate-ledger-pulse bg-signal" />
    </div>
  );
}
